import { getNominative } from '@lmc/common/utils'
import type {
  Address,
  Home,
  HomeDto,
  RotaPatternResponse
} from '@lmc/ts-common'
import { Heading, Tag } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { upperCase } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import ServiceUser from 'screens/Rostering/components/ServiceUser'
import SkillsContainer from 'screens/Rostering/components/SkillsContainer'
import { SHIFT, VISIT } from 'screens/Rostering/constants'
import { getFundingSourceName } from 'screens/Rostering/utils'
import styled from 'styled-components'
import { getNewAddressById, type RotaPatternModalType } from '../utils'
import NeedsContainer from './NeedsContainer'

export type PopulatedRotaPatternResponse = RotaPatternResponse & {
  address?: Address[]
}

type GeneralInformationProps = {
  home?: HomeDto
  pattern: PopulatedRotaPatternResponse
  type: RotaPatternModalType
}

export const GeneralRotaInformation = ({
  home,
  pattern,
  type
}: GeneralInformationProps) => {
  const { t } = useTranslation()

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  const flags = useFeatureFlags({
    flagNames: ['rostering_parked_features']
  })?.data

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  return (
    <MainWrapper>
      <InfoWrapper>
        <Heading level={2}>{pattern.title}</Heading>
        <StyledText>
          {pattern.instanceType} {pattern.fundingTypeId ? '·' : ''} {` `}
          {getFundingSourceName(
            {
              type: pattern.fundingTypeId ?? '',
              hoursPerWeek: 0
            },
            home as HomeDto
          )}
        </StyledText>
        {type === VISIT && flagsRosteringParkedFeaturesEnabled && (
          <StyledBadges>
            <Tag color="green">{upperCase(t('VISIT'))}</Tag>
            <Tag color="purple">
              {pattern.flexiType
                ? upperCase(t('FLEXI'))
                : upperCase(t('FIXED'))}
            </Tag>
          </StyledBadges>
        )}
      </InfoWrapper>
      {pattern?.slotNotes && (
        <SectionWrapper>
          <Section>
            <StyledLabel level={2}>{t('DESCRIPTION')}</StyledLabel>
            <Text>{pattern.slotNotes}</Text>
          </Section>
        </SectionWrapper>
      )}
      {type === VISIT && (
        <SectionWrapper>
          <Section>
            <StyledLabel level={2}>{residentNominative}</StyledLabel>
            <ServiceUser resident={pattern.resident!} />
          </Section>
          <Section>
            <StyledLabel level={2}>{t('ADDRESS')}</StyledLabel>
            <Text>
              {getNewAddressById(
                pattern.address ?? [],
                pattern?.addressId ?? ''
              )}
            </Text>
          </Section>
        </SectionWrapper>
      )}
      <SectionWrapper>
        {type === SHIFT && (
          <Section>
            <StyledLabel level={2}>{t('SKILLS_TO_COVER')}</StyledLabel>
            <SkillsContainer skillsIds={pattern?.skills || []} />
          </Section>
        )}
        {type === VISIT && (
          <Section>
            <StyledLabel level={2}>{`${residentNominative} ${t(
              'NEEDS'
            )}`}</StyledLabel>
            <NeedsContainer residentId={pattern?.subjectId} />
          </Section>
        )}
      </SectionWrapper>
    </MainWrapper>
  )
}

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
const StyledLabel = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledBadges = styled.div`
  display: flex;
  padding-top: 8px;
  height: fit-content;
`

const SectionWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
`
